if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.mqlVertical = window.matchMedia('(min-width: 900px)');
        this._syncingFromScroll = false;
        this._scrollSyncTimer = null;

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });

        this.initThumbnailNavigation();
        this.initVerticalThumbHeightSync();

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      isVerticalThumbnails() {
        return this.dataset.thumbnailsLayout === 'vertical' && this.mqlVertical.matches;
      }

      getThumbnailItems() {
        if (!this.elements.thumbnails) return [];
        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
      }

      getThumbnailList() {
        return this.elements.thumbnails?.querySelector('.thumbnail-list') || this.elements.thumbnails?.slider || null;
      }

      getThumbNavButtons() {
        if (!this.elements.thumbnails) return { prev: null, next: null };
        return {
          prev: this.elements.thumbnails.querySelector(':scope > button[name="previous"]'),
          next: this.elements.thumbnails.querySelector(':scope > button[name="next"]'),
        };
      }

      initVerticalThumbHeightSync() {
        if (!this.elements.viewer || !this.elements.thumbnails) return;

        this.syncVerticalThumbHeight = this.syncVerticalThumbHeight.bind(this);

        if (typeof ResizeObserver !== 'undefined') {
          this._viewerResizeObserver = new ResizeObserver(() => this.syncVerticalThumbHeight());
          this._viewerResizeObserver.observe(this.elements.viewer);
        }

        window.addEventListener('resize', this.syncVerticalThumbHeight, { passive: true });

        if (typeof this.mqlVertical.addEventListener === 'function') {
          this.mqlVertical.addEventListener('change', this.syncVerticalThumbHeight);
        } else if (typeof this.mqlVertical.addListener === 'function') {
          this.mqlVertical.addListener(this.syncVerticalThumbHeight);
        }

        this.elements.viewer.querySelectorAll('img').forEach((img) => {
          if (!img.complete) img.addEventListener('load', this.syncVerticalThumbHeight, { once: true });
        });

        requestAnimationFrame(() => this.syncVerticalThumbHeight());
        window.setTimeout(() => this.syncVerticalThumbHeight(), 150);
        window.setTimeout(() => this.syncVerticalThumbHeight(), 500);
      }

      syncVerticalThumbHeight() {
        const thumbs = this.elements.thumbnails;
        const viewer = this.elements.viewer;
        if (!thumbs || !viewer) return;

        if (!this.isVerticalThumbnails()) {
          this.style.removeProperty('--pdp-gallery-viewer-height');
          thumbs.style.height = '';
          thumbs.style.maxHeight = '';
          return;
        }

        const height = Math.round(viewer.getBoundingClientRect().height);
        if (height < 1) return;

        this.style.setProperty('--pdp-gallery-viewer-height', `${height}px`);
        thumbs.style.height = `${height}px`;
        thumbs.style.maxHeight = `${height}px`;
      }

      initThumbnailNavigation() {
        if (!this.elements.thumbnails) return;

        const { prev, next } = this.getThumbNavButtons();
        const list = this.getThumbnailList();

        const onNav = (direction) => (event) => {
          if (!this.isVerticalThumbnails()) return;
          const button = event.currentTarget;
          if (button?.disabled || button?.getAttribute('aria-disabled') === 'true') return;
          event.preventDefault();
          event.stopImmediatePropagation();
          this.stepThumbnailMedia(direction);
        };

        prev?.addEventListener('click', onNav(-1), true);
        next?.addEventListener('click', onNav(1), true);

        if (list) {
          const scheduleScrollSync = () => {
            if (!this.isVerticalThumbnails() || this._syncingFromScroll) return;
            clearTimeout(this._scrollSyncTimer);
            this._scrollSyncTimer = setTimeout(() => this.syncMediaFromThumbnailScroll(), 60);
          };

          list.addEventListener('scroll', scheduleScrollSync, { passive: true });
          list.addEventListener('scrollend', () => {
            if (!this.isVerticalThumbnails() || this._syncingFromScroll) return;
            this.syncMediaFromThumbnailScroll();
          });

          list.addEventListener(
            'wheel',
            (event) => {
              if (!this.isVerticalThumbnails()) return;
              if (Math.abs(event.deltaY) < 1) return;
              event.stopPropagation();
              scheduleScrollSync();
            },
            { passive: true }
          );
        }

        const armVerticalSlider = () => {
          if (!this.elements.thumbnails) return;
          if (this.isVerticalThumbnails()) {
            this.elements.thumbnails.update = () => this.updateThumbnailNavState();
            this.elements.thumbnails.onButtonClick = (event) => {
              event.preventDefault();
              const dir = event.currentTarget?.name === 'next' ? 1 : -1;
              this.stepThumbnailMedia(dir);
            };
          }
          this.updateThumbnailNavState();
        };
        armVerticalSlider();
        requestAnimationFrame(armVerticalSlider);
        window.setTimeout(armVerticalSlider, 100);
        window.setTimeout(armVerticalSlider, 400);

        const refresh = () => {
          armVerticalSlider();
          this.syncVerticalThumbHeight();
        };
        if (typeof this.mqlVertical.addEventListener === 'function') {
          this.mqlVertical.addEventListener('change', refresh);
        } else if (typeof this.mqlVertical.addListener === 'function') {
          this.mqlVertical.addListener(refresh);
        }
      }

      updateThumbnailNavState() {
        if (!this.elements.thumbnails || !this.isVerticalThumbnails()) return;

        const items = this.getThumbnailItems();
        const { prev, next } = this.getThumbNavButtons();
        const canNavigate = items.length > 1;

        const activeIndex = items.findIndex((item) => item.querySelector('[aria-current="true"]'));
        const index = activeIndex >= 0 ? activeIndex : 0;

        if (prev) {
          const disablePrev = !canNavigate || index <= 0;
          prev.disabled = disablePrev;
          prev.setAttribute('aria-disabled', disablePrev ? 'true' : 'false');
        }
        if (next) {
          const disableNext = !canNavigate || index >= items.length - 1;
          next.disabled = disableNext;
          next.setAttribute('aria-disabled', disableNext ? 'true' : 'false');
        }
      }

      stepThumbnailMedia(direction) {
        if (!this.isVerticalThumbnails()) return;

        const items = this.getThumbnailItems();
        if (items.length < 2) return;

        const activeIndex = items.findIndex((item) => item.querySelector('[aria-current="true"]'));
        const currentIndex = activeIndex >= 0 ? activeIndex : 0;
        const nextIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
        if (nextIndex === currentIndex) return;

        const target = items[nextIndex];
        if (!target?.dataset?.target) return;

        this.setActiveMedia(target.dataset.target, false);
        this.scrollThumbnailIntoView(target);
        this.updateThumbnailNavState();
      }

      syncMediaFromThumbnailScroll() {
        if (!this.isVerticalThumbnails()) return;
        const list = this.getThumbnailList();
        const items = this.getThumbnailItems();
        if (!list || items.length < 2) return;

        const listRect = list.getBoundingClientRect();
        const anchor = listRect.top + listRect.height * 0.35;

        let best = null;
        let bestDist = Infinity;
        items.forEach((item) => {
          const rect = item.getBoundingClientRect();
          if (rect.height < 1) return;
          const mid = rect.top + rect.height / 2;
          const dist = Math.abs(mid - anchor);
          if (dist < bestDist) {
            bestDist = dist;
            best = item;
          }
        });

        if (!best?.dataset?.target) return;
        const active = this.elements.viewer.querySelector('.product__media-item.is-active');
        if (active?.dataset?.mediaId === best.dataset.target) {
          this.updateThumbnailNavState();
          return;
        }

        this._syncingFromScroll = true;
        this.setActiveMedia(best.dataset.target, false);
        this._syncingFromScroll = false;
        this.updateThumbnailNavState();
      }

      scrollThumbnailIntoView(thumbnail) {
        const list = this.getThumbnailList();
        if (!list || !thumbnail) return;

        if (this.isVerticalThumbnails()) {
          const top = thumbnail.offsetTop - (list.clientHeight - thumbnail.clientHeight) / 2;
          list.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          return;
        }

        list.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          if (activeThumbnail?.dataset?.mediaPosition) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }
        }

        this.updateThumbnailNavState();
        this.syncVerticalThumbHeight();
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('[data-target] button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button')?.setAttribute('aria-current', true);

        if (this._syncingFromScroll) {
          this.updateThumbnailNavState();
          return;
        }

        if (this.isVerticalThumbnails()) {
          const list = this.getThumbnailList();
          if (!list) return;
          const thumbTop = thumbnail.offsetTop;
          const thumbBottom = thumbTop + thumbnail.offsetHeight;
          const viewTop = list.scrollTop;
          const viewBottom = viewTop + list.clientHeight;
          if (thumbTop < viewTop + 8 || thumbBottom > viewBottom - 8) {
            this.scrollThumbnailIntoView(thumbnail);
          }
          this.updateThumbnailNavState();
          return;
        }

        if (this.elements.thumbnails.isSlideVisible?.(thumbnail, 10)) return;
        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

/**
 * Fallback binder for vertical thumbnail arrows.
 * Works even if media-gallery was already defined from a stale/cached script.
 */
(function bindVerticalThumbnailArrowsFallback() {
  const MQL = window.matchMedia('(min-width: 900px)');

  function isVerticalGallery(gallery) {
    return gallery?.dataset?.thumbnailsLayout === 'vertical' && MQL.matches;
  }

  function getItems(thumbs) {
    return Array.from(thumbs.querySelectorAll('[data-target]'));
  }

  function setActive(gallery, mediaId) {
    if (typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, false);
      return;
    }
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;
    viewer.querySelectorAll('[data-media-id]').forEach((el) => el.classList.remove('is-active'));
    const active = viewer.querySelector(`[data-media-id="${mediaId}"]`);
    active?.classList.add('is-active');
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    thumbs?.querySelectorAll('[data-target] button').forEach((btn) => btn.removeAttribute('aria-current'));
    thumbs?.querySelector(`[data-target="${mediaId}"] button`)?.setAttribute('aria-current', 'true');
  }

  function step(gallery, direction) {
    if (!isVerticalGallery(gallery)) return;
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!thumbs) return;
    const items = getItems(thumbs);
    if (items.length < 2) return;
    const activeIndex = items.findIndex((item) => item.querySelector('[aria-current="true"]'));
    const currentIndex = activeIndex >= 0 ? activeIndex : 0;
    const nextIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));
    if (nextIndex === currentIndex) return;
    const target = items[nextIndex];
    if (!target?.dataset?.target) return;
    setActive(gallery, target.dataset.target);
    const list = thumbs.querySelector('.thumbnail-list');
    if (list) {
      const top = target.offsetTop - (list.clientHeight - target.clientHeight) / 2;
      list.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
    updateDisabled(gallery);
  }

  function updateDisabled(gallery) {
    if (!isVerticalGallery(gallery)) return;
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!thumbs) return;
    const items = getItems(thumbs);
    const prev = thumbs.querySelector(':scope > button[name="previous"]');
    const next = thumbs.querySelector(':scope > button[name="next"]');
    const activeIndex = items.findIndex((item) => item.querySelector('[aria-current="true"]'));
    const index = activeIndex >= 0 ? activeIndex : 0;
    if (prev) prev.disabled = items.length < 2 || index <= 0;
    if (next) next.disabled = items.length < 2 || index >= items.length - 1;
    if (thumbs && typeof thumbs.update === 'function') {
      thumbs.update = function () {
        updateDisabled(gallery);
      };
    }
  }

  function bindGallery(gallery) {
    if (!gallery || gallery.dataset.verticalThumbFallback === 'true') return;
    if (gallery.dataset.thumbnailsLayout !== 'vertical') return;
    gallery.dataset.verticalThumbFallback = 'true';

    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    if (!thumbs) return;
    const prev = thumbs.querySelector(':scope > button[name="previous"]');
    const next = thumbs.querySelector(':scope > button[name="next"]');

    const handle = (direction) => (event) => {
      if (!isVerticalGallery(gallery)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (typeof gallery.stepThumbnailMedia === 'function') {
        gallery.stepThumbnailMedia(direction);
        gallery.updateThumbnailNavState?.();
        return;
      }
      step(gallery, direction);
    };

    prev?.addEventListener('click', handle(-1), true);
    next?.addEventListener('click', handle(1), true);

    updateDisabled(gallery);
    requestAnimationFrame(() => updateDisabled(gallery));
    window.setTimeout(() => updateDisabled(gallery), 200);
  }

  function bindAll() {
    document.querySelectorAll('media-gallery[data-thumbnails-layout="vertical"]').forEach(bindGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAll);
  } else {
    bindAll();
  }
  document.addEventListener('shopify:section:load', bindAll);
  if (typeof MQL.addEventListener === 'function') {
    MQL.addEventListener('change', bindAll);
  } else if (typeof MQL.addListener === 'function') {
    MQL.addListener(bindAll);
  }
})();
